import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  VirtualizedFormSelect,
  Checkbox,
  CollapsibleSection,
} from '@roger/ui-components';
import type { KinnserClinicDataResponse } from '../../services/api/types.gen';
import { Field, DateField } from '../../components/FormFields';
import { AiExtractedInsuranceSuggestion } from '../../components/intake/AiExtractedInsuranceSuggestion';
import {
  PatientDetailsSection,
  EmergencyContactSection,
} from '../../components/intake/shared';
import {
  ADMISSION_TYPE_OPTIONS,
  HCPCS_CODE_OPTIONS,
} from '../../hooks/useCreatePatientForm';
import type { AddressValidationResult } from '../../hooks/useCreatePatientForm/types';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';
import {
  ClinicSelect,
  type ClinicSelectClinic,
} from '../../components/clinic/ClinicSelect';

interface KinnserCreateEpisodeStepFormContentProps {
  // Form controls
  control: Control<CreateEpisodeStepFormData>;
  register: UseFormRegister<CreateEpisodeStepFormData>;
  errors: FieldErrors<CreateEpisodeStepFormData>;
  setValue: UseFormSetValue<CreateEpisodeStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Display values
  displayValues: {
    hhCahpsOptOut?: boolean;
    kinnserLanguageKey?: string;
  };

  // Save status
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  saveToServer: (data: Partial<CreateEpisodeStepFormData>) => void;

  // Clinics
  clinics: ClinicSelectClinic[];
  clinicData: KinnserClinicDataResponse | null;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  insuranceOptions: { name: string; id: string }[];

  // Intake status
  selectedIntake: {
    status: string;
    primary_insurance?: string | null;
    fields_explanations?: Record<string, unknown> | null;
    is_new_patient?: boolean | null;
  } | null;
  showAIExplanations?: boolean;
  documentNameDict?: Record<string, string>;

  // Address validation
  addressSuggestion: AddressValidationResult | null;
  addressModalOpen: boolean;
  setAddressModalOpen: (open: boolean) => void;
  isValidatingAddress: boolean;
  handleAddressFieldBlur: (
    fieldNameOrEvent: React.FocusEvent<HTMLElement>
  ) => void;
  handleAcceptAddressSuggestion: () => void;

  // Clinic field handler
  handleClinicFieldBlur: () => void;

  // Language key for conditional rendering
  kinnserLanguageKey: string | null | undefined;

  // Form validation state
  isFormValid: boolean;
}

export const KinnserCreateEpisodeStepFormContent: React.FC<
  KinnserCreateEpisodeStepFormContentProps
> = ({
  control,
  register,
  errors,
  setValue,
  handleFieldBlur,
  saveToServer,
  clinics,
  clinicData,
  loadingClinics,
  loadingClinicData,
  insuranceOptions,
  selectedIntake,
  showAIExplanations = false,
  documentNameDict,
  isValidatingAddress,
  handleAddressFieldBlur,
  kinnserLanguageKey,
}) => {
  const hasTransferredFromAnotherHha = useWatch({
    control,
    name: 'has_transferred_from_another_hha',
    defaultValue: false,
  });

  return (
    <form className='space-y-5' onSubmit={e => e.preventDefault()}>
      {/* Kinnser Assignment */}
      <CollapsibleSection title='Kinnser Assignment'>
        <div className='grid grid-cols-4 gap-3'>
          {/* Row 1: Clinic & Branch */}
          <ClinicSelect
            label='Clinic'
            placeholder={
              loadingClinics ? 'Loading clinics...' : 'Select clinic...'
            }
            clinics={clinics}
            required
            disabled
            error={errors.agency_clinic_id?.message}
            {...register('agency_clinic_id')}
          />

          <VirtualizedFormSelect
            label='Branch'
            placeholder={
              loadingClinicData ? 'Loading branches...' : 'Select branch...'
            }
            options={clinicData?.branches || []}
            required
            disabled={loadingClinicData}
            error={errors.kinnser_branch_id?.message}
            {...register('kinnser_branch_id', {
              onBlur: handleFieldBlur,
            })}
            hidden={Boolean((clinicData?.branches?.length ?? 0) <= 1)}
          />

          <VirtualizedFormSelect
            label='Physician'
            placeholder={
              loadingClinicData
                ? 'Loading physicians...'
                : 'Select physician...'
            }
            options={clinicData?.physicians || []}
            sortOptions
            required
            disabled={loadingClinicData}
            error={errors.kinnser_physician_id?.message}
            {...register('kinnser_physician_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <div>
            <VirtualizedFormSelect
              label='Insurance Provider'
              placeholder={
                loadingClinicData
                  ? 'Loading insurance providers...'
                  : 'Select insurance...'
              }
              options={insuranceOptions}
              required
              disabled
              error={errors.kinnser_insurance_provider_id?.message}
              {...register('kinnser_insurance_provider_id')}
            />
            <AiExtractedInsuranceSuggestion
              value={selectedIntake?.primary_insurance}
              showAIExplanations={showAIExplanations}
              fieldExplanation={
                selectedIntake?.fields_explanations?.primary_insurance
              }
              fieldsExplanations={selectedIntake?.fields_explanations}
              documentNameDict={documentNameDict}
            />
          </div>

          {/* Row 2: Admission details */}
          <VirtualizedFormSelect
            label='Admission Type'
            placeholder='Select type...'
            options={ADMISSION_TYPE_OPTIONS}
            required
            disabled={loadingClinics || loadingClinicData}
            error={errors.kinnser_admission_type?.message}
            {...register('kinnser_admission_type', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='HCPCS Code'
            placeholder='Select code...'
            options={HCPCS_CODE_OPTIONS}
            disabled={loadingClinics || loadingClinicData || false}
            required
            error={errors.kinnser_hcpcs_code_key?.message}
            {...register('kinnser_hcpcs_code_key', {
              onBlur: handleFieldBlur,
            })}
          />

          <Field
            label='MRN'
            htmlFor='kinnserMrn'
            disabled={
              clinicData?.auto_mrn ||
              loadingClinics ||
              loadingClinicData ||
              false
            }
            required={!clinicData?.auto_mrn}
            error={
              !clinicData?.auto_mrn ? errors.kinnser_mrn?.message : undefined
            }
            inputProps={{
              ...register('kinnser_mrn', {
                onBlur: handleFieldBlur,
              }),
              ...(clinicData?.auto_mrn ? { placeholder: 'Auto-assigned' } : {}),
            }}
          />

          {/* Medicaid field hidden for now */}
          {/* <Field
                label='Medicaid Number'
                htmlFor='medicaidNumber'
                disabled={loadingClinics || loadingClinicData}
                inputProps={register('medicaid_id', {
                  onBlur: handleFieldBlur,
                })}
              /> */}

          <div className='col-span-4 flex items-center gap-2 pt-1'>
            <Controller
              name='kinnser_hh_cahps_opt_out'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id='hhCahpsOptOut'
                  checked={value || false}
                  disabled={loadingClinics || loadingClinicData}
                  onCheckedChange={checked => {
                    onChange(checked);
                    if (checked) {
                      saveToServer({
                        kinnser_hh_cahps_opt_out: true,
                      });
                    } else {
                      saveToServer({
                        kinnser_hh_cahps_opt_out: false,
                      });
                    }
                  }}
                />
              )}
            />
            <label
              htmlFor='hhCahpsOptOut'
              className='text-sm font-medium cursor-pointer'
            >
              HH CAHPS Opt Out
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Transfer & Face-to-Face */}
      <CollapsibleSection title='Transfer & Face-to-Face'>
        <div className='grid grid-cols-4 gap-3'>
          <div className='col-span-4 flex items-center gap-2 pt-1'>
            <Controller
              name='has_transferred_from_another_hha'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id='hasTransferredFromAnotherHhaKinnser'
                  checked={value ?? false}
                  onCheckedChange={checked => {
                    const newVal = checked === true;
                    onChange(newVal);
                    if (!newVal) {
                      setValue('prior_hha', null);
                      saveToServer({
                        has_transferred_from_another_hha: false,
                        prior_hha: null,
                      });
                    } else {
                      saveToServer({
                        has_transferred_from_another_hha: true,
                      });
                    }
                  }}
                />
              )}
            />
            <label
              htmlFor='hasTransferredFromAnotherHhaKinnser'
              className='text-sm font-medium cursor-pointer'
            >
              Transferred from another HHA
            </label>
          </div>
          {hasTransferredFromAnotherHha === true && (
            <Controller
              name='prior_hha'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Field
                  label='Prior HHA'
                  htmlFor='prior_hha_kinnser'
                  inputProps={{
                    value: value ?? '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e.target.value || null);
                      saveToServer({
                        prior_hha: e.target.value || null,
                      });
                    },
                    onBlur: handleFieldBlur,
                  }}
                />
              )}
            />
          )}
          <Controller
            name='f2f_encounter_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='f2f_encounter_date'
                label='Face-to-Face Encounter Date'
                value={value}
                htmlFor='f2f_encounter_date_kinnser'
                onChange={val => {
                  onChange(val);
                  saveToServer({ f2f_encounter_date: val });
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 10,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />
        </div>
      </CollapsibleSection>

      {/* Patient Details - Combined patient info and contact */}
      <PatientDetailsSection
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        handleFieldBlur={handleFieldBlur}
        saveToServer={saveToServer}
        isValidatingAddress={isValidatingAddress}
        handleAddressFieldBlur={handleAddressFieldBlur}
        kinnserLanguageKey={kinnserLanguageKey}
      />

      {/* Emergency Contact - optional; pushed to WellSky when create/onboard */}
      <EmergencyContactSection
        control={control}
        register={register}
        errors={errors}
        handleFieldBlur={handleFieldBlur}
      />
    </form>
  );
};
